import React, { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X, Search } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SearchableSelectOption {
  value: string
  label: string
  searchText?: string // дополнительный текст для поиска (например, табельный номер)
}

export interface SearchableSelectProps {
  value: string | null
  onChange: (value: string | null) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyOption?: string // текст для пустого значения ("Не назначен")
  emptySearchText?: string // текст когда ничего не найдено
  disabled?: boolean
  className?: string
  isLoading?: boolean
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Выберите...',
  searchPlaceholder = 'Поиск...',
  emptyOption = 'Не выбрано',
  emptySearchText = 'Ничего не найдено',
  disabled = false,
  className,
  isLoading = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Найти текущую выбранную опцию
  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find(opt => opt.value === value) || null
  }, [value, options])

  // Фильтрация опций по поисковому запросу
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options

    const query = searchQuery.toLowerCase().trim()
    return options.filter(opt => {
      const searchIn = (opt.searchText || opt.label).toLowerCase()
      return searchIn.includes(query)
    })
  }, [options, searchQuery])

  // Обновление позиции dropdown
  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target)
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target)

      if (isOutsideTrigger && isOutsideDropdown) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Фокус на поле поиска при открытии и обновление позиции
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition()
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }, [isOpen])

  // Обновление позиции при скролле/ресайзе
  useEffect(() => {
    if (!isOpen) return

    const handleUpdate = () => updateDropdownPosition()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [isOpen])

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const handleSelect = (optionValue: string | null) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  const handleToggle = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchQuery('')
      }
    }
  }

  // Dropdown content
  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] rounded-md border bg-popover text-popover-foreground shadow-lg"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {/* Search Input */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              'flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm',
              'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring'
            )}
          />
        </div>
      </div>

      {/* Options List */}
      <div className="max-h-60 overflow-y-auto p-1">
        {/* Loading state */}
        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            Загрузка...
          </div>
        ) : (
          <>
            {/* Empty option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm text-left',
                'hover:bg-accent hover:text-accent-foreground',
                !value && 'bg-accent/50'
              )}
            >
              <span className="text-muted-foreground">{emptyOption}</span>
            </button>

            {/* Filtered options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm text-left',
                    'hover:bg-accent hover:text-accent-foreground',
                    value === option.value && 'bg-accent'
                  )}
                >
                  {option.label}
                </button>
              ))
            ) : options.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Нет доступных сотрудников
              </div>
            ) : searchQuery ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {emptySearchText}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )

  return (
    <div className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled || isLoading}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && 'ring-2 ring-ring ring-offset-2'
        )}
      >
        <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
          {isLoading ? 'Загрузка...' : (selectedOption?.label || placeholder)}
        </span>
        <div className="flex items-center gap-1">
          {selectedOption && !disabled && (
            <X
              className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={cn('h-4 w-4 opacity-50 transition-transform', isOpen && 'rotate-180')} />
        </div>
      </button>

      {/* Portal for dropdown to escape dialog overflow */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  )
}
